import { useId } from 'react';
import { EmptyState } from '@pazportal/ui';
import { useActiveTrip } from '../hooks/useActiveTrip';
import { useHardwareLifecycle, type LifecycleAction, type LifecycleAsset } from '../hooks/useHardwareLifecycle';
import { useSolutionTabs } from '../hooks/useSolutionTabs';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { TabPanel, TabStrip } from '../components/Tabs';
import { ErrorBlock, LoadingBlock, NoActiveTrip } from '../components/PageState';
import { TripPicker } from '../components/TripPicker';
import { EM_DASH, formatDate, pluralize } from '../lib/formatters';

const PILL: Record<LifecycleAction, { label: string; className: string }> = {
  act_now: { label: 'Act now', className: 'bg-error-soft text-error' },
  renew: { label: 'Warranty', className: 'bg-warning-soft text-warning' },
  schedule_update: { label: 'Update available', className: 'bg-info-soft text-info' },
  insufficient_data: { label: 'Data missing', className: 'bg-surface-3 text-text-muted' },
  none: { label: 'No action', className: 'bg-success-soft text-success' },
};

const ACTION_ORDER: LifecycleAction[] = ['act_now', 'renew', 'schedule_update', 'insufficient_data', 'none'];

function actionText(a: LifecycleAsset): string {
  switch (a.lifecycleAction) {
    case 'act_now':
      return 'Update firmware and renew warranty';
    case 'renew':
      return a.warrantyStatus === 'expired' ? 'Renew warranty (expired)' : 'Renew warranty (expiring)';
    case 'schedule_update':
      return 'Schedule firmware update';
    case 'insufficient_data':
      return [a.warrantyStatus === 'unknown' ? 'warranty expiry' : null, a.firmwareStatus === 'unknown' ? 'latest known firmware' : null]
        .filter((part): part is string => Boolean(part))
        .reduce((acc, part, i) => (i === 0 ? `Record ${part}` : `${acc} and ${part}`), '');
    default:
      return 'No action needed';
  }
}

function warrantyText(a: LifecycleAsset): { text: string; className: string } {
  if (!a.warranty_expiry || a.warrantyDays === null) return { text: 'Not recorded', className: 'text-text-muted' };
  const date = formatDate(a.warranty_expiry);
  if (a.warrantyDays < 0) return { text: `${date} · expired ${pluralize(-a.warrantyDays, 'day')} ago`, className: 'font-semibold text-error' };
  if (a.warrantyStatus === 'expiring') return { text: `${date} · ${pluralize(a.warrantyDays, 'day')} left`, className: 'font-semibold text-warning' };
  return { text: date, className: '' };
}

function firmwareText(a: LifecycleAsset): string {
  if (a.firmwareStatus === 'current') return 'Current';
  if (a.firmwareStatus === 'update_available') return `Update to ${a.firmware_latest_known}`;
  return a.firmware_version ? 'Latest known version not recorded' : 'Installed version not recorded';
}

export function LifecyclePage() {
  const { data: trip, trips, isLoading, isError, error, refetch, selectTrip } = useActiveTrip();
  const lifecycle = useHardwareLifecycle(trip?.location?.id);
  const { active, setActive, tabs } = useSolutionTabs(lifecycle.data?.solutionTypes, lifecycle.data?.byType);
  const isDesktop = useIsDesktop();
  const prefix = useId();

  if (isLoading) return <LoadingBlock label="Loading your trips…" />;
  if (!trip) {
    return <NoActiveTrip trips={trips} onSelect={selectTrip} error={isError ? error : undefined} onRetry={() => void refetch()} />;
  }

  const header = (
    <div className="space-y-4">
      <div className="min-w-0">
        <h1 className="text-xl font-bold text-text">Hardware lifecycle</h1>
        <p className="mt-0.5 truncate text-sm text-text-muted">{trip.location?.name ?? trip.customer?.name ?? trip.project_number}</p>
      </div>
      {trips.length > 1 && <TripPicker trips={trips} selectedId={trip.id} onSelect={selectTrip} />}
    </div>
  );

  if (lifecycle.isLoading) {
    return (
      <div className="space-y-4">
        {header}
        <LoadingBlock label="Loading assets…" />
      </div>
    );
  }
  if (lifecycle.isError || !lifecycle.data) {
    return (
      <div className="space-y-4">
        {header}
        <ErrorBlock title="Could not load assets" error={lifecycle.error} onRetry={() => void lifecycle.refetch()} />
      </div>
    );
  }

  const data = lifecycle.data;
  if (data.assets.length === 0 || !active) {
    return (
      <div className="space-y-4">
        {header}
        <EmptyState
          icon={
            <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          }
          title="No assets recorded for this site"
          subtitle="Warranty and firmware tracking starts once assets are added to a deployment at this location."
        />
      </div>
    );
  }

  const list = data.byType[active] ?? [];
  const counts = ACTION_ORDER.map((action) => ({ action, count: list.filter((a) => a.lifecycleAction === action).length })).filter((c) => c.count > 0);
  const actNow = counts.find((c) => c.action === 'act_now')?.count ?? 0;

  return (
    <div className="space-y-4">
      {header}
      <TabStrip tabs={tabs} value={active} onChange={setActive} label="Assets by solution type" idPrefix={prefix} />
      <TabPanel prefix={prefix} tabKey={active} className="space-y-3">
        {actNow > 0 && (
          <p role="alert" className="rounded-lg border border-error/40 bg-error-soft px-4 py-3 text-sm text-error">
            {pluralize(actNow, 'device')} {actNow === 1 ? 'has' : 'have'} an expired warranty and outdated firmware.
          </p>
        )}
        <ul className="flex flex-wrap gap-2" aria-label="Lifecycle summary">
          {counts.map((c) => (
            <li key={c.action} className={`rounded-full px-2.5 py-1 text-xs font-medium ${PILL[c.action].className}`}>
              {c.count} {PILL[c.action].label.toLowerCase()}
            </li>
          ))}
        </ul>
        {isDesktop ? <LifecycleTable assets={list} /> : <LifecycleCards assets={list} />}
        {counts.some((c) => c.action === 'insufficient_data') && (
          <p className="text-xs text-text-muted">&ldquo;Data missing&rdquo; means the warranty expiry or the latest known firmware is not recorded, so no recommendation is made.</p>
        )}
      </TabPanel>
    </div>
  );
}

function Pill({ action }: { action: LifecycleAction }) {
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${PILL[action].className}`}>{PILL[action].label}</span>;
}

function LifecycleTable({ assets }: { assets: LifecycleAsset[] }) {
  return (
    <div className="card overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">ID</th>
            <th scope="col">Device</th>
            <th scope="col">Firmware</th>
            <th scope="col">Latest known</th>
            <th scope="col">Warranty</th>
            <th scope="col">Lifecycle</th>
            <th scope="col">Recommended action</th>
          </tr>
        </thead>
        <tbody>
          {assets.map((a) => {
            const warranty = warrantyText(a);
            return (
              <tr key={a.id}>
                <td className="font-mono text-xs">{a.asset_id_label ?? EM_DASH}</td>
                <td>
                  <p className="font-medium">{a.device_name ?? EM_DASH}</p>
                  <p className="text-xs text-text-muted">{[a.make, a.model].filter(Boolean).join(' ')}</p>
                </td>
                <td className="font-mono text-xs">{a.firmware_version ?? EM_DASH}</td>
                <td className="font-mono text-xs">{a.firmware_latest_known ?? <span className="font-sans text-text-muted">Not recorded</span>}</td>
                <td className={warranty.className}>{warranty.text}</td>
                <td>
                  <Pill action={a.lifecycleAction} />
                </td>
                <td className="text-xs">{actionText(a)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LifecycleCards({ assets }: { assets: LifecycleAsset[] }) {
  return (
    <ul className="data-cards">
      {assets.map((a) => {
        const warranty = warrantyText(a);
        return (
          <li key={a.id} className="data-card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-text">{a.device_name ?? a.asset_id_label ?? 'Unnamed asset'}</p>
                <p className="truncate text-xs text-text-muted">{[a.make, a.model].filter(Boolean).join(' ') || EM_DASH}</p>
              </div>
              <Pill action={a.lifecycleAction} />
            </div>
            <dl>
              <dt>Asset ID</dt>
              <dd className="font-mono">{a.asset_id_label ?? EM_DASH}</dd>
              <dt>Firmware</dt>
              <dd>
                <span className="font-mono">{a.firmware_version ?? EM_DASH}</span>
                <span className="block text-text-muted">{firmwareText(a)}</span>
              </dd>
              <dt>Warranty</dt>
              <dd className={warranty.className}>{warranty.text}</dd>
              <dt>Action</dt>
              <dd>{actionText(a)}</dd>
            </dl>
          </li>
        );
      })}
    </ul>
  );
}
